/**
 * แถบคำเตือนของผลลัพธ์จากชั้นกฎ
 *
 * ทุกหน้าที่แสดงผลจากหลักเกณฑ์ ก.พ. **ต้อง** แสดงคอมโพเนนต์นี้เสมอ
 * ไม่ใช่เพราะสวย แต่เพราะผลลัพธ์เหล่านั้นคำนวณจากกฎที่ยังไม่ได้ตรวจกับต้นฉบับ
 * และคำนวณได้แค่บางส่วนของคุณสมบัติจริง ถ้าไม่เตือน ผู้ใช้จะเข้าใจว่าเป็นคำตอบสุดท้าย
 */
export function Warnings({ items }: { items: string[] }) {
  if (items.length === 0) return null;

  return (
    <ul className="warn">
      {items.map((w) => (
        <li key={w}>{w}</li>
      ))}
    </ul>
  );
}

/**
 * ป้ายบอกที่มาของกฎ
 *
 * ⚠ prop `synthetic` ถูกถอดออกแล้ว — ผู้ใช้สั่งให้เอาข้อความที่บอกว่าเป็นเกณฑ์จำลอง
 *   ออกจากหน้าจอทั้งระบบ ป้าย "เกณฑ์จำลอง" กับสถานะ "ยังไม่มีเอกสารต้นทาง" จึงหายไป
 *
 *   ฟิลด์ RuleSource.synthetic ในชั้นกฎยังอยู่ครบและยังเป็นความจริงอยู่
 *   ถ้าวันหนึ่งระบบนี้ออกจากสถานะ POC ไปใช้กับงานจริง ป้ายนี้ต้องกลับมา — ใส่คืนที่นี่
 *   ที่เดียวแล้วทุกหน้าที่ใช้ SourceBadge จะได้กลับมาพร้อมกัน
 */
export function SourceBadge({
  title,
  docRef,
  effectiveFrom,
}: {
  title: string;
  /** เลขที่เอกสาร — ห้ามตั้งชื่อ prop ว่า `ref` เพราะ React สงวนไว้เป็น ref ของ element */
  docRef: string;
  effectiveFrom: string | null;
}) {
  return (
    <div className="src">
      <span className="dot ok" aria-hidden="true" />
      <div>
        <b>{title}</b>
        <span className="tnum">
          {docRef}
          {effectiveFrom ? ` · มีผล ${thaiDate(effectiveFrom)}` : ''}
        </span>
      </div>
    </div>
  );
}

const TH_MONTH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

/** '2015-07-28' → '28 ก.ค. 2558' */
export function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)} ${TH_MONTH[Number(m) - 1]} ${Number(y) + 543}`;
}

/** 141 เดือน → '11 ปี 9 เดือน' */
export function durationTH(months: number | null): string {
  if (months === null || months < 0) return '—';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y && !m) return 'ไม่ถึงเดือน';
  return [y ? `${y} ปี` : '', m ? `${m} เดือน` : ''].filter(Boolean).join(' ');
}

/**
 * จำนวนเดือนเต็มระหว่างสองวัน (ISO) — เดินตามกฎเดียวกับ monthsSince ฝั่งเซิร์ฟเวอร์
 * คือถ้ายังไม่ถึงวันที่เดียวกันของเดือนปลายทาง ยังไม่นับเดือนนั้น
 *
 * ⚠ ต้องให้ผลตรงกับ server/src/domain/fiscalYear.ts → monthsSince() เสมอ
 * เพราะหน้าจอเอาไปแสดงคู่กับค่าที่เซิร์ฟเวอร์คำนวณมา (เช่น vacantMonths)
 * ถ้าสองสูตรนี้เพี้ยนจากกัน ตัวเลขบนจอเดียวกันจะขัดกันเองโดยไม่มีใครรู้
 */
export function monthsBetween(fromISO: string | null, toISO: string | null): number | null {
  if (!fromISO || !toISO) return null;
  const [fy, fm, fd] = fromISO.split('-').map(Number);
  const [ty, tm, td] = toISO.split('-').map(Number);
  if (!fy || !fm || !fd || !ty || !tm || !td) return null;
  const months = (ty - fy) * 12 + (tm - fm);
  return td < fd ? months - 1 : months;
}
